
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {EventType} from './EventType';
import {FieldModelEventType} from './EventType';

/**
 * EmailTemplate struct
 */
class EmailTemplate {
  /**
   * Initialize struct
   * @param {!UUID=} id
   * @param {!string=} name
   * @param {!string=} comment
   * @param {!string=} description
   * @param {!EventType=} eventType
   * @param {!string=} emailSubject
   * @param {!string=} emailBody
   * @constructor
   */
  constructor (id = UUID.sequential(), name = '', comment = '', description = '', eventType = new EventType(), emailSubject = '', emailBody = '') {
    this.id = id
    this.Name = name
    this.Comment = comment
    this.Description = description
    this.EventType = eventType
    this.EmailSubject = emailSubject
    this.EmailBody = emailBody
  }

  /**
   * Copy struct (shallow copy)
   * @this {!EmailTemplate}
   * @param {!EmailTemplate} other Other struct
   * @returns {!EmailTemplate} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = new UUID(other.id)
    } else {
      this.id = null
    }
    if (other.Name != null) {
      this.Name = other.Name
    } else {
      this.Name = null
    }
    if (other.Comment != null) {
      this.Comment = other.Comment
    } else {
      this.Comment = null
    }
    if (other.Description != null) {
      this.Description = other.Description
    } else {
      this.Description = null
    }
    if (other.EventType != null) {
      this.EventType = EventType.fromObject(other.EventType)
    } else {
      this.EventType = null
    }
    if (other.EmailSubject != null) {
      this.EmailSubject = other.EmailSubject
    } else {
      this.EmailSubject = null
    }
    if (other.EmailBody != null) {
      this.EmailBody = other.EmailBody
    } else {
      this.EmailBody = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!EmailTemplate}
   * @returns {!EmailTemplate} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new EmailTemplateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new EmailTemplateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!EmailTemplate}
   * @param {!EmailTemplate} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof EmailTemplate)) {
      throw new TypeError('Instance of EmailTemplate is required!')
    }
    // noinspection RedundantIfStatementJS
    if (this.id != null) {
      if ((other.id == null) || !this.id.eq(other.id)) {
        return false
      }
    } else if (other.id != null) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!EmailTemplate}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id.toString() : null),
      Name: ((this.Name != null) ? this.Name : null),
      Comment: ((this.Comment != null) ? this.Comment : null),
      Description: ((this.Description != null) ? this.Description : null),
      EventType: ((this.EventType != null) ? this.EventType : null),
      EmailSubject: ((this.EmailSubject != null) ? this.EmailSubject : null),
      EmailBody: ((this.EmailBody != null) ? this.EmailBody : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return EmailTemplate.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!EmailTemplate} other Object value
   * @returns {!EmailTemplate} Created struct
   */
  static fromObject (other) {
    return new EmailTemplate().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!EmailTemplate}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return EmailTemplate.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!EmailTemplate}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 95
  }
}

export { EmailTemplate };

/**
 * Fast Binary Encoding EmailTemplate field model
 */
class FieldModelEmailTemplate extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.Name = new fbe.FieldModelString(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.Comment = new fbe.FieldModelString(buffer, this.Name.fbeOffset + this.Name.fbeSize)
    this.Description = new fbe.FieldModelString(buffer, this.Comment.fbeOffset + this.Comment.fbeSize)
    this.EventType = new FieldModelEventType(buffer, this.Description.fbeOffset + this.Description.fbeSize)
    this.EmailSubject = new fbe.FieldModelString(buffer, this.EventType.fbeOffset + this.EventType.fbeSize)
    this.EmailBody = new fbe.FieldModelString(buffer, this.EmailSubject.fbeOffset + this.EmailSubject.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelEmailTemplate}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelEmailTemplate}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.Name.fbeSize + this.Comment.fbeSize + this.Description.fbeSize + this.EventType.fbeSize + this.EmailSubject.fbeSize + this.EmailBody.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelEmailTemplate}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.Name.fbeExtra + this.Comment.fbeExtra + this.Description.fbeExtra + this.EventType.fbeExtra + this.EmailSubject.fbeExtra + this.EmailBody.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelEmailTemplate}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelEmailTemplate.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelEmailTemplate}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 95
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelEmailTemplate}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelEmailTemplate}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Comment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.EventType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EventType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EventType.fbeSize

    if ((fbeCurrentSize + this.EmailSubject.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EmailSubject.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailSubject.fbeSize

    if ((fbeCurrentSize + this.EmailBody.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EmailBody.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailBody.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelEmailTemplate}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelEmailTemplate}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelEmailTemplate}
   * @param {!EmailTemplate} fbeValue Default value, defaults is new EmailTemplate()
   * @returns {!EmailTemplate} EmailTemplate value
   */
  get (fbeValue = new EmailTemplate()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelEmailTemplate}
   * @param {!EmailTemplate} fbeValue EmailTemplate value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get(UUID.sequential())
    } else {
      fbeValue.id = UUID.sequential()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) <= fbeStructSize) {
      fbeValue.Comment = this.Comment.get()
    } else {
      fbeValue.Comment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.EventType.fbeSize) <= fbeStructSize) {
      fbeValue.EventType = this.EventType.get()
    } else {
      fbeValue.EventType = new EventType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EventType.fbeSize

    if ((fbeCurrentSize + this.EmailSubject.fbeSize) <= fbeStructSize) {
      fbeValue.EmailSubject = this.EmailSubject.get()
    } else {
      fbeValue.EmailSubject = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailSubject.fbeSize

    if ((fbeCurrentSize + this.EmailBody.fbeSize) <= fbeStructSize) {
      fbeValue.EmailBody = this.EmailBody.get()
    } else {
      fbeValue.EmailBody = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EmailBody.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelEmailTemplate}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelEmailTemplate}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelEmailTemplate}
   * @param {!EmailTemplate} fbeValue EmailTemplate value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelEmailTemplate}
   * @param {EmailTemplate} fbeValue EmailTemplate value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.Name.set(fbeValue.Name)
    this.Comment.set(fbeValue.Comment)
    this.Description.set(fbeValue.Description)
    this.EventType.set(fbeValue.EventType)
    this.EmailSubject.set(fbeValue.EmailSubject)
    this.EmailBody.set(fbeValue.EmailBody)
  }
}

export { FieldModelEmailTemplate };

/**
 * Fast Binary Encoding EmailTemplate model
 */
class EmailTemplateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelEmailTemplate(this.buffer, 4)
  }

  /**
   * Get the EmailTemplate model
   * @this {!EmailTemplateModel}
   * @returns {!FieldModelEmailTemplate} model EmailTemplate model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!EmailTemplateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!EmailTemplateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return EmailTemplateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!EmailTemplateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelEmailTemplate.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!EmailTemplateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!EmailTemplateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!EmailTemplateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!EmailTemplateModel}
   * @param {!EmailTemplate} value EmailTemplate value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!EmailTemplateModel}
   * @param {!EmailTemplate} value EmailTemplate value, defaults is new EmailTemplate()
   * @return {!object} Deserialized EmailTemplate value and its size
   */
  deserialize (value = new EmailTemplate()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new EmailTemplate(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new EmailTemplate(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!EmailTemplateModel}
   * @param {!number} prev Previous EmailTemplate model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { EmailTemplateModel };
